import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import FormWithValidation from "../../components/common/FormWithValidation";
import Loader from "../../components/common/Loader";
import { ControlsService } from "../../api/generated";
import { withApiData } from "../../api/openapiClient";
import { useToast } from "../../components/common/ToastProvider";
import DestructiveConfirm from "../../components/common/DestructiveConfirm";
import { useDestructiveAction } from "../../destructiveActions";
import Input from "../../ui/Input";
import Select from "../../ui/Select";
import Textarea from "../../ui/Textarea";
import ControlEffectivenessBadge from "../../components/controls/ControlEffectivenessBadge";
import { useControlEffectivenessConfig } from "../../hooks/useControlEffectivenessConfig";
import { buildControlEffectivenessOptions } from "../../utils/controlEffectiveness";
import { colors, radius, shadows, spacing, typography } from "../../ui/tokens";
import {
  pageHeaderStyle as sharedPageHeaderStyle,
  pageHeaderTitleBlockStyle,
  pageShellStyle as sharedPageShellStyle,
  pageSubtitleStyle as sharedPageSubtitleStyle,
  pageTitleStyle as sharedPageTitleStyle,
  surfaceCardStyle,
} from "../../ui/pagePatterns";
import { getRiskById, getRisks } from "../../riskService";
import { listControlAssignmentUsers } from "../../api/controls.api.js";
import { listTaxonomyValues } from "../../api/taxonomy.api";

const CONTROL_LABEL_OPTIONS = ["Preventative", "Detective", "Corrective"];

const baseSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional().nullable(),
  category: z.string().optional().nullable(),
  control_labels: z.array(z.enum(CONTROL_LABEL_OPTIONS)).default([]),
  control_category_id: z.string().optional().nullable(),
  owner_user_id: z.string().optional().nullable(),
  assignee_user_id: z.string().optional().nullable(),
  catalog_id: z.string().optional().nullable(),
  effectiveness_rating: z.string().optional().nullable(),
  last_review_date: z.string().optional().nullable(),
  last_reviewed_by: z.string().optional().nullable(),
});

const buildUserOptions = (items, currentValue) => {
  const options = [];
  const seen = new Set();
  const list = Array.isArray(items) ? items : [];
  list.forEach((user) => {
    const value = user?.id != null ? String(user.id) : "";
    if (!value || seen.has(value)) return;
    const label = user?.username
      ? `${user.username}${user.email ? ` (${user.email})` : ""}`
      : user?.email || `User #${user.id}`;
    seen.add(value);
    options.push({ value, label, username: user?.username ? String(user.username) : "" });
  });
  if (currentValue && !seen.has(String(currentValue))) {
    options.unshift({ value: String(currentValue), label: String(currentValue), username: String(currentValue) });
  }
  return options;
};

const filterOptions = (options, query) => {
  const term = String(query || "").trim().toLowerCase();
  if (!term) return options;
  return options.filter((opt) => String(opt.label || opt.value || "").toLowerCase().includes(term));
};

const pageShellStyle = {
  ...sharedPageShellStyle,
  gap: spacing.xl,
};

const pageHeaderStyle = {
  ...sharedPageHeaderStyle,
  alignItems: "center",
};

const headerBlockStyle = {
  ...pageHeaderTitleBlockStyle,
  maxWidth: 760,
};

const pageTitleStyle = {
  ...sharedPageTitleStyle,
};

const pageSubtitleStyle = {
  ...sharedPageSubtitleStyle,
};

const formCardStyle = {
  ...surfaceCardStyle,
  padding: spacing.xl + spacing.sm,
  display: "grid",
  gap: spacing.lg,
  boxShadow: shadows.sm,
};

const warningBannerStyle = {
  border: `1px solid ${colors.warningBorder}`,
  background: colors.warningSubtle,
  color: colors.warning,
  borderRadius: radius.sm,
  padding: `${spacing.sm}px ${spacing.md}px`,
  fontSize: typography.size.xs,
  display: "grid",
  gap: spacing.xs,
};

const formSectionsStyle = {
  display: "grid",
  gap: spacing.lg,
};

const formSectionStyle = {
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  background: colors.surfaceSubtle,
  padding: spacing.md + spacing.sm,
  display: "grid",
  gap: spacing.md,
};

const sectionHeaderStyle = {
  display: "grid",
  gap: spacing.xs,
};

const sectionTitleStyle = {
  margin: 0,
  color: colors.text,
  fontSize: typography.size.base,
  fontWeight: typography.weight.medium,
};

const sectionGridStyle = {
  display: "grid",
  gap: spacing.md,
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  alignItems: "start",
};

const fullWidthFieldStyle = {
  gridColumn: "1 / -1",
};

const subtleHelperTextStyle = {
  fontSize: typography.size.xs,
  color: colors.textMuted,
};

const inlineErrorTextStyle = {
  color: colors.danger,
  fontSize: typography.size.xs,
};

const chipWrapStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: spacing.sm,
};

const chipStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: spacing.xs,
  padding: `${spacing.xs}px ${spacing.md + spacing.xs}px`,
  borderRadius: radius.pill,
  border: `1px solid ${colors.border}`,
  background: colors.surface,
  color: colors.text,
  fontSize: typography.size.xs,
  maxWidth: "100%",
};

const chipRemoveButtonStyle = {
  border: "none",
  background: "transparent",
  cursor: "pointer",
  fontSize: typography.size.xs,
  padding: 0,
  lineHeight: 1,
  color: colors.textMuted,
};

const checkboxFieldStyle = {
  display: "grid",
  gap: spacing.sm,
};

const checkboxGroupStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: spacing.sm,
};

const checkboxItemStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: spacing.xs,
  padding: `${spacing.xs}px ${spacing.sm}px`,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.pill,
  background: colors.surface,
  fontSize: typography.size.sm,
  color: colors.text,
};

const fieldHeadingStyle = {
  fontSize: typography.size.sm,
  color: colors.text,
  fontWeight: typography.weight.medium,
};

const formActionCss = `
  .control-form-card .form-with-validation {
    display: grid;
    gap: ${spacing.xl}px;
  }
  .control-form-card .form-fields {
    display: grid;
    gap: ${spacing.xl}px;
  }
  .control-form-card .form-actions {
    margin-top: 0 !important;
    padding-top: ${spacing.lg}px;
    border-top: 1px solid ${colors.border};
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    row-gap: ${spacing.sm}px;
  }
  @media (max-width: 768px) {
    .control-form-card .form-actions {
      justify-content: flex-start;
    }
  }
`;

export default function ControlForm() {
  const { cid } = useParams();
  const isEdit = Boolean(cid);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();
  const [serverFieldErrors, setServerFieldErrors] = useState({});
  const [confirmPruneOpen, setConfirmPruneOpen] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(null);
  const { action: linkPruneAction } = useDestructiveAction("CONTROL_LINK_PRUNE_ON_UPDATE");
  const sourceRiskId = location?.state?.sourceRiskId ? String(location.state.sourceRiskId) : "";
  const sourceRiskName = location?.state?.sourceRiskName || "";
  const [sourceRiskLabel, setSourceRiskLabel] = useState(sourceRiskName);
  const [riskOptions, setRiskOptions] = useState([]);
  const [risksLoading, setRisksLoading] = useState(false);
  const [risksError, setRisksError] = useState(null);
  const [riskPickerValue, setRiskPickerValue] = useState("");
  const [userOptions, setUserOptions] = useState([]);
  const [userSearchOwner, setUserSearchOwner] = useState("");
  const [userSearchAssignee, setUserSearchAssignee] = useState("");
  const [controlCategories, setControlCategories] = useState([]);
  const [categoryError, setCategoryError] = useState("");
  const { items: effectivenessConfig } = useControlEffectivenessConfig();

  const schema = useMemo(() => {
    if (isEdit) {
      return baseSchema;
    }
    return baseSchema.extend({
      risk_ids: z.array(z.string()).min(1, "At least one Risk is required to create a Control."),
    });
  }, [isEdit]);

  const { data, isLoading } = useQuery({
    queryKey: ["control", cid],
    queryFn: () =>
      withApiData(
        ControlsService.getControlControlsControlIdGet({
          controlId: Number(cid),
        })
      ),
    enabled: isEdit,
  });

  useEffect(() => {
    if (isEdit) return;
    if (!sourceRiskId || sourceRiskLabel) return;
    getRiskById(sourceRiskId)
      .then((res) => {
        const risk = res?.data || res;
        if (risk?.name) {
          setSourceRiskLabel(risk.name);
        }
      })
      .catch(() => null);
  }, [isEdit, sourceRiskId, sourceRiskLabel]);

  useEffect(() => {
    let active = true;
    const loadUsers = async () => {
      try {
        const res = await listControlAssignmentUsers();
        const payload = res?.data || [];
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.items)
            ? payload.items
            : [];
        if (active) {
          setUserOptions(buildUserOptions(list));
        }
      } catch (err) {
        if (active) {
          setUserOptions([]);
        }
      }
    };
    loadUsers();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    const loadCategories = async () => {
      setCategoryError("");
      try {
        const res = await listTaxonomyValues("control_category");
        const payload = res?.data || res;
        if (active) {
          setControlCategories(Array.isArray(payload) ? payload : []);
        }
      } catch (err) {
        if (active) {
          setControlCategories([]);
          setCategoryError(err?.response?.data?.detail || err?.message || "Unable to load categories.");
        }
      }
    };
    loadCategories();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (isEdit) return;
    let active = true;
    const loadRisks = async () => {
      setRisksLoading(true);
      setRisksError(null);
      try {
        const res = await getRisks({ limit: 200 });
        const payload = res?.data || res;
        const items = Array.isArray(payload?.items) ? payload.items : Array.isArray(payload) ? payload : [];
        const options = items
          .filter((r) => r?.id)
          .map((r) => ({
            value: String(r.id),
            label: r.name ? `${r.name} (#${r.id})` : `Risk #${r.id}`,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        if (sourceRiskId && !options.some((opt) => opt.value === sourceRiskId)) {
          const fallbackLabel = sourceRiskLabel ? `${sourceRiskLabel} (#${sourceRiskId})` : `Risk #${sourceRiskId}`;
          options.unshift({ value: sourceRiskId, label: fallbackLabel });
        }
        if (active) {
          setRiskOptions(options);
        }
      } catch (err) {
        if (active) {
          setRiskOptions([]);
          setRisksError(err?.response?.data?.detail || err?.response?.data?.error || err?.message || "Unable to load risks.");
        }
      } finally {
        if (active) {
          setRisksLoading(false);
        }
      }
    };
    loadRisks();
    return () => {
      active = false;
    };
  }, [isEdit, sourceRiskId, sourceRiskLabel]);

  const mutation = useMutation({
    mutationFn: (payload) =>
      isEdit
        ? withApiData(
            ControlsService.updateControlControlsControlIdPut({
              controlId: Number(cid),
              requestBody: payload,
            })
          )
        : withApiData(
            ControlsService.createControlControlsPost({
              requestBody: payload,
            })
          ),
    onSuccess: (result) => {
      const saved = result?.data || result;
      success(isEdit ? "Control updated" : "Control created");
      queryClient.invalidateQueries({ queryKey: ["controls"] });
      if (saved?.id) {
        queryClient.invalidateQueries({ queryKey: ["control", saved.id] });
        navigate(`/controls/${saved.id}`);
      } else {
        navigate("/controls");
      }
    },
    onError: (err) => {
      setServerFieldErrors(err?.fieldErrors || {});
      showError(err?.detail || "Failed to save control");
    },
  });

  const defaultValues = useMemo(
    () => ({
      name: data?.name || "",
      description: data?.description || "",
      category: data?.category || "",
      control_labels: Array.isArray(data?.control_labels)
        ? data.control_labels.filter((label) => CONTROL_LABEL_OPTIONS.includes(label))
        : [],
      control_category_id: data?.control_category_id ? String(data.control_category_id) : "",
      owner_user_id: data?.owner_user_id ? String(data.owner_user_id) : "",
      assignee_user_id: data?.assignee_user_id ? String(data.assignee_user_id) : "",
      catalog_id: data?.catalog_id || "",
      risk_ids: !isEdit && sourceRiskId ? [sourceRiskId] : [],
      effectiveness_rating: "",
      last_review_date: "",
      last_reviewed_by: "",
    }),
    [data, isEdit, sourceRiskId]
  );

  const breadcrumbItems = [
    { label: "Controls", to: "/controls" },
    { label: isEdit ? "Edit Control" : "New Control" },
  ];

  const requestSubmit = (values, methods) => {
    const normalized = { ...values };
    ["owner_user_id", "assignee_user_id", "control_category_id"].forEach((field) => {
      if (normalized[field] === "" || normalized[field] === undefined) {
        normalized[field] = null;
      } else if (normalized[field] !== null) {
        normalized[field] = Number(normalized[field]);
      }
    });
    if (!isEdit) {
      const riskIds = Array.isArray(normalized.risk_ids)
        ? normalized.risk_ids.map((id) => String(id)).filter(Boolean)
        : [];
      if (riskIds.length === 0) {
        methods?.setError("risk_ids", {
          type: "manual",
          message: "At least one Risk is required to create a Control.",
        });
        return;
      }
      if (normalized.effectiveness_rating) {
        if (!normalized.last_review_date) {
          methods?.setError("last_review_date", {
            type: "manual",
            message: "Provide last review date when setting effectiveness.",
          });
        }
        if (!normalized.last_reviewed_by) {
          methods?.setError("last_reviewed_by", {
            type: "manual",
            message: "Provide reviewer when setting effectiveness.",
          });
        }
        if (!normalized.last_review_date || !normalized.last_reviewed_by) {
          return;
        }
      } else {
        normalized.last_review_date = null;
        normalized.last_reviewed_by = null;
      }
      mutation.mutate({
        ...normalized,
        risk_ids: riskIds.map((id) => Number(id)),
      });
      return;
    }
    const { risk_ids: _riskIds, ...payload } = normalized;
    setPendingSubmit(payload);
    setConfirmPruneOpen(true);
  };

  return (
    <div style={pageShellStyle}>
      <style>{formActionCss}</style>
      <Breadcrumbs items={breadcrumbItems} />
      <div style={pageHeaderStyle}>
        <div style={headerBlockStyle}>
          <h1 style={pageTitleStyle}>{isEdit ? "Edit Control" : "New Control"}</h1>
          <p style={pageSubtitleStyle}>
            {isEdit ? "Update control details and assignments" : "Create a control and assign ownership"}
          </p>
        </div>
      </div>

      <div className="control-form-card" style={formCardStyle}>
        {isEdit && isLoading && <Loader />}
        {isEdit && linkPruneAction && (
          <div style={warningBannerStyle}>
            <div style={{ fontWeight: typography.weight.medium }}>Implicit replacement</div>
            <div>{linkPruneAction.description}</div>
            <div>Scope: {linkPruneAction.scope}</div>
            <div>Omitted linked risks will be removed when you save.</div>
          </div>
        )}
        <FormWithValidation
          schema={schema}
          defaultValues={defaultValues}
          onSubmit={(values, methods) => requestSubmit(values, methods)}
          onCancel={() => navigate(isEdit ? `/controls/${cid}` : "/controls")}
          serverFieldErrors={serverFieldErrors}
          submitLabel={isEdit ? "Save" : "Create"}
          submitDisabled={(formState) => mutation.isLoading || (!isEdit && !formState.isValid)}
        >
          {({ register, formState, setValue, watch }) => {
            const riskIds = watch("risk_ids") || [];
            const selectedCategory = watch("control_category_id") || "";
            const selectedControlLabelsRaw = watch("control_labels");
            const selectedControlLabels = Array.isArray(selectedControlLabelsRaw)
              ? selectedControlLabelsRaw.filter((label) => CONTROL_LABEL_OPTIONS.includes(label))
              : [];
            const ownerOptions = [{ value: "", label: "Unassigned" }, ...userOptions];
            const filteredOwnerOptions = filterOptions(ownerOptions, userSearchOwner);
            const filteredAssigneeOptions = filterOptions(ownerOptions, userSearchAssignee);
            const reviewerOptions = [{ value: "", label: "Select reviewer" }, ...userOptions
              .filter((option) => option.username)
              .map((option) => ({ value: option.username, label: option.label }))];
            const activeCategories = (Array.isArray(controlCategories) ? controlCategories : []).filter(
              (item) => String(item?.status || "active").toLowerCase() === "active"
            );
            const selectedCategoryOption =
              selectedCategory && !activeCategories.some((item) => String(item.id) === String(selectedCategory))
                ? controlCategories.find((item) => String(item?.id) === String(selectedCategory))
                : null;
            const categoryOptions = (selectedCategoryOption ? [selectedCategoryOption, ...activeCategories] : activeCategories)
              .map((item) => ({
                value: String(item.id),
                label: item.label || item.value || `Category #${item.id}`,
              }));
            const effectivenessOptions = buildControlEffectivenessOptions(effectivenessConfig);
            const addRiskSelection = (riskId) => {
              if (!riskId) return;
              if (riskIds.includes(riskId)) return;
              const next = [...riskIds, riskId];
              setValue("risk_ids", next, { shouldValidate: true, shouldDirty: true });
            };
            const removeRiskSelection = (riskId) => {
              if (sourceRiskId && riskId === sourceRiskId) return;
              const next = riskIds.filter((id) => id !== riskId);
              setValue("risk_ids", next, { shouldValidate: true, shouldDirty: true });
            };
            const toggleControlLabel = (label) => {
              if (!CONTROL_LABEL_OPTIONS.includes(label)) return;
              const next = selectedControlLabels.includes(label)
                ? selectedControlLabels.filter((item) => item !== label)
                : [...selectedControlLabels, label];
              setValue("control_labels", next, { shouldValidate: true, shouldDirty: true });
            };
            const selectedEffectiveness = watch("effectiveness_rating") || "";

            return (
              <div style={formSectionsStyle}>
                {!isEdit && (
                  <section style={formSectionStyle}>
                    <div style={sectionHeaderStyle}>
                      <h2 style={sectionTitleStyle}>Risk Context</h2>
                    </div>
                    <div style={sectionGridStyle}>
                      {sourceRiskId && (
                        <div style={{ ...subtleHelperTextStyle, ...fullWidthFieldStyle }}>
                          Created from Risk: {sourceRiskLabel || `Risk #${sourceRiskId}`}
                        </div>
                      )}
                      <div style={fullWidthFieldStyle}>
                        <Select
                          label="Risk *"
                          value={riskPickerValue}
                          onChange={(e) => {
                            const selected = e.target.value;
                            setRiskPickerValue("");
                            addRiskSelection(selected);
                          }}
                          disabled={risksLoading || riskOptions.length === 0}
                        >
                          <option value="">Select a risk</option>
                          {riskOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </Select>
                      </div>
                      {risksError && riskOptions.length === 0 && (
                        <div style={{ ...inlineErrorTextStyle, ...fullWidthFieldStyle }}>{risksError}</div>
                      )}
                      {formState.errors.risk_ids?.message && (
                        <div style={{ ...inlineErrorTextStyle, ...fullWidthFieldStyle }}>
                          {formState.errors.risk_ids.message}
                        </div>
                      )}
                      {riskIds.length > 0 && (
                        <div style={{ ...chipWrapStyle, ...fullWidthFieldStyle }}>
                          {riskIds.map((riskId) => {
                            const match = riskOptions.find((opt) => String(opt.value) === String(riskId));
                            const label = match?.label || `Risk #${riskId}`;
                            const isLocked = sourceRiskId && riskId === sourceRiskId;
                            return (
                              <span
                                key={riskId}
                                style={{
                                  ...chipStyle,
                                  ...(isLocked
                                    ? {
                                        borderColor: colors.borderStrong,
                                        background: colors.surfaceSubtle,
                                        color: colors.textMuted,
                                      }
                                    : null),
                                }}
                              >
                                {label}
                                {!isLocked && (
                                  <button
                                    type="button"
                                    onClick={() => removeRiskSelection(riskId)}
                                    aria-label={`Remove ${label}`}
                                    style={chipRemoveButtonStyle}
                                  >
                                    X
                                  </button>
                                )}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </section>
                )}

                <section style={formSectionStyle}>
                  <div style={sectionHeaderStyle}>
                    <h2 style={sectionTitleStyle}>Control Details</h2>
                  </div>
                  <div style={sectionGridStyle}>
                    <div style={fullWidthFieldStyle}>
                      <Input
                        label="Name"
                        data-testid="control-name"
                        error={formState.errors.name?.message}
                        {...register("name")}
                      />
                    </div>
                    <div style={fullWidthFieldStyle}>
                      <Textarea
                        label="Description"
                        data-testid="control-description"
                        error={formState.errors.description?.message}
                        {...register("description")}
                      />
                    </div>
                    <Select
                      label="Control Category"
                      data-testid="control-category"
                      value={watch("control_category_id") || ""}
                      onChange={(e) => setValue("control_category_id", e.target.value, { shouldDirty: true })}
                      options={categoryOptions}
                      emptyLabel={categoryError || "No active categories"}
                    />
                    <div style={checkboxFieldStyle}>
                      <span style={fieldHeadingStyle}>Control Labels</span>
                      <div style={checkboxGroupStyle}>
                        {CONTROL_LABEL_OPTIONS.map((label) => (
                          <label key={label} style={checkboxItemStyle}>
                            <input
                              type="checkbox"
                              checked={selectedControlLabels.includes(label)}
                              onChange={() => toggleControlLabel(label)}
                            />
                            <span>{label}</span>
                          </label>
                        ))}
                      </div>
                      <span style={subtleHelperTextStyle}>Select all that apply.</span>
                    </div>
                    <Input
                      label="Catalog ID"
                      {...register("catalog_id")}
                    />
                  </div>
                </section>

                <section style={formSectionStyle}>
                  <div style={sectionHeaderStyle}>
                    <h2 style={sectionTitleStyle}>Ownership & Assignment</h2>
                  </div>
                  <div style={sectionGridStyle}>
                    <Input
                      label="Owner search"
                      value={userSearchOwner}
                      onChange={(e) => setUserSearchOwner(e.target.value)}
                      placeholder="Search users..."
                    />
                    <Select
                      label="Owner"
                      value={watch("owner_user_id") || ""}
                      onChange={(e) => setValue("owner_user_id", e.target.value, { shouldDirty: true })}
                      options={filteredOwnerOptions}
                      emptyLabel="No users available"
                    />
                    <Input
                      label="Assignee search"
                      value={userSearchAssignee}
                      onChange={(e) => setUserSearchAssignee(e.target.value)}
                      placeholder="Search users..."
                    />
                    <Select
                      label="Assignee"
                      value={watch("assignee_user_id") || ""}
                      onChange={(e) => setValue("assignee_user_id", e.target.value, { shouldDirty: true })}
                      options={filteredAssigneeOptions}
                      emptyLabel="No users available"
                    />
                  </div>
                </section>

                {!isEdit && (
                  <section style={formSectionStyle}>
                    <div style={sectionHeaderStyle}>
                      <h2 style={sectionTitleStyle}>Governance</h2>
                      <div style={subtleHelperTextStyle}>
                        Effectiveness is optional. If you set it, review date and reviewer are required.
                      </div>
                    </div>
                    <div style={sectionGridStyle}>
                      <div style={{ display: "grid", gap: spacing.xs }}>
                        <Select
                          label="Effectiveness rating"
                          value={selectedEffectiveness}
                          onChange={(e) => {
                            const nextValue = e.target.value;
                            setValue("effectiveness_rating", nextValue, { shouldValidate: true, shouldDirty: true });
                            if (!nextValue) {
                              setValue("last_review_date", "", { shouldValidate: true, shouldDirty: true });
                              setValue("last_reviewed_by", "", { shouldValidate: true, shouldDirty: true });
                            }
                          }}
                          options={effectivenessOptions}
                          error={formState.errors.effectiveness_rating?.message}
                        />
                        <ControlEffectivenessBadge value={selectedEffectiveness} config={effectivenessConfig} />
                      </div>
                      {selectedEffectiveness && (
                        <>
                          <Input
                            label="Last review date"
                            type="date"
                            value={watch("last_review_date") || ""}
                            onChange={(e) => setValue("last_review_date", e.target.value, { shouldValidate: true, shouldDirty: true })}
                            error={formState.errors.last_review_date?.message}
                          />
                          <Select
                            label="Last reviewed by"
                            value={watch("last_reviewed_by") || ""}
                            onChange={(e) => setValue("last_reviewed_by", e.target.value, { shouldValidate: true, shouldDirty: true })}
                            options={reviewerOptions}
                            error={formState.errors.last_reviewed_by?.message}
                          />
                        </>
                      )}
                    </div>
                  </section>
                )}
              </div>
            );
          }}
        </FormWithValidation>
      </div>
      <DestructiveConfirm
        actionId="CONTROL_LINK_PRUNE_ON_UPDATE"
        open={confirmPruneOpen}
        resourceDisplayName={data?.name}
        context={{ scopeLabel: "Saving control updates may prune unlinked risks." }}
        onCancel={() => {
          setConfirmPruneOpen(false);
          setPendingSubmit(null);
        }}
        onConfirm={() => {
          const payload = pendingSubmit;
          setConfirmPruneOpen(false);
          setPendingSubmit(null);
          if (payload) {
            mutation.mutate(payload);
          }
        }}
      />
    </div>
  );
}
